import React, {ReactElement, useContext, useState} from 'react';
import {TableRow} from '@material-ui/core';
import {Trans} from 'react-i18next';
import {NxButton, NxButtonVariant, NxCheckbox} from '@nextbank/ui-components';
import {CBSLoanDetails} from '../../../../../../../shared/model/cbs-loans';
import {ConsolidatedLoansContext} from '../consolidated-loans-context';
import {CashHelper} from '../../../../../../../utils/cash-helper';
import {StepContext} from '../../loan-application-step/LoanApplicationStep';
import {getCheckboxValue} from '../../../../../../../utils/input-utils';
import TableDataCell from '../../../../../../shared/table/cells/TableDataCell';
import WaiveTable from '../waive/WaiveTable';
import styles from './ConsolidatedLoansTableRow.module.scss';
import {WaiveInputContext} from '../waive/waive-input-context';

const HEADERS_TRANS_PREFIX = 'LOAN_CONFIGURATIONS.LOAN_APPLICATION_DATA.CONSOLIDATED_LOANS';
const COMMON_TRANS_PREFIX = 'COMMON';

interface Props {
  cbsLoanDetails: CBSLoanDetails;
  rowNumber?: number;
  hasActionButton?: boolean;
  disableActionButton?: boolean;
  isCustomerLoansSelection?: boolean;
  showReleaseButton?: boolean;
  hasWaiveCheckBox?: boolean;
  isLoading?: boolean;
  onReleaseProduct?: (value?: number) => void;
}

const ConsolidatedLoansTableRow = ({
                                     cbsLoanDetails,
                                     rowNumber,
                                     hasActionButton,
                                     isCustomerLoansSelection = false,
                                     disableActionButton,
                                     showReleaseButton = false,
                                     hasWaiveCheckBox = false,
                                     isLoading,
                                     onReleaseProduct
                                   }: Props): ReactElement => {
  const {removeLoan, addLoan} = useContext(ConsolidatedLoansContext);
  const {removeWaiveInput, waiveInputs} = useContext(WaiveInputContext);
  const {isStepReadonly} = useContext(StepContext);
  const [waiveLoading, setWaiveLoading] = useState<boolean>(true);
  const hasWaiveInput = waiveInputs.some(waive => waive.loanId === cbsLoanDetails.loanId);
  const [showWaive, setShowWaive] = useState<boolean>(hasWaiveInput);

  const onCheckBoxChange = (event: React.ChangeEvent): void => {
    const isChecked = getCheckboxValue(event);
    setShowWaive(isChecked);
    if (!isChecked && cbsLoanDetails.loanId) {
      removeWaiveInput(cbsLoanDetails.loanId);
      setWaiveLoading(true);
    }
  };

  return (
    <>
      <TableRow className={styles.tableRow}>
        <TableDataCell data={rowNumber || ''} />
        {
          isCustomerLoansSelection ? (
            <>
              <TableDataCell data={cbsLoanDetails.productName} />
              <TableDataCell data={cbsLoanDetails.productNumber} />
              <TableDataCell data={cbsLoanDetails.status} />
              <TableDataCell data={cbsLoanDetails.daysLate} />
              <TableDataCell data={cbsLoanDetails.paymentInterval} />
              <TableDataCell data={cbsLoanDetails.maturityDate} />
              <TableDataCell data={cbsLoanDetails.totalAmortizationNumber} />
              <TableDataCell data={cbsLoanDetails.averageAmortizationAmount && CashHelper.addCurrencyMask(cbsLoanDetails.averageAmortizationAmount)} />
              <TableDataCell data={cbsLoanDetails.principalBalance && CashHelper.addCurrencyMask(cbsLoanDetails.principalBalance)} />
              <TableDataCell data={cbsLoanDetails.interestBalance && CashHelper.addCurrencyMask(cbsLoanDetails.interestBalance)} />
              <TableDataCell data={cbsLoanDetails.cbuAmount && CashHelper.addCurrencyMask(cbsLoanDetails.cbuAmount)} />
              <TableDataCell data={cbsLoanDetails.pfAmount && CashHelper.addCurrencyMask(cbsLoanDetails.pfAmount)} />
              <TableDataCell data={cbsLoanDetails.tpAmount && CashHelper.addCurrencyMask(cbsLoanDetails.tpAmount)} />
              <TableDataCell data={cbsLoanDetails.pastDueInterestAmount && CashHelper.addCurrencyMask(cbsLoanDetails.pastDueInterestAmount)} />
              <TableDataCell data={cbsLoanDetails.penaltiesAmount && CashHelper.addCurrencyMask(cbsLoanDetails.penaltiesAmount)} />
              <TableDataCell data={cbsLoanDetails.customFeesAmount && CashHelper.addCurrencyMask(cbsLoanDetails.customFeesAmount)} />
              {hasActionButton &&
                <TableDataCell data={
                  <NxButton className={styles.removeButton}
                            disabled={disableActionButton || isStepReadonly}
                            variant={NxButtonVariant.OUTLINED}
                            onClick={(): void => addLoan(cbsLoanDetails)}>
                    <Trans>{`${COMMON_TRANS_PREFIX}.ADD`}</Trans>
                  </NxButton>
                } />
              }
            </>
          ) : (
            <>
              <TableDataCell withMinWidth wrapText data={cbsLoanDetails.customerName} />
              <TableDataCell withMinWidth wrapText data={cbsLoanDetails.productNumber} />
              <TableDataCell withMinWidth wrapText data={cbsLoanDetails.principalBalance && <>
                {CashHelper.addCurrencyMask(cbsLoanDetails.principalBalance)}</>
              } />
              <TableDataCell withMinWidth
                             wrapText
                             data={CashHelper.addCurrencyMask(cbsLoanDetails.interestBalance || 0)} />
              <TableDataCell withMinWidth wrapText data={CashHelper.addCurrencyMask(cbsLoanDetails.cbuAmount)} />
              <TableDataCell withMinWidth wrapText data={CashHelper.addCurrencyMask(cbsLoanDetails.pfAmount)} />
              <TableDataCell withMinWidth wrapText data={CashHelper.addCurrencyMask(cbsLoanDetails.tpAmount)} />
              <TableDataCell withMinWidth
                             wrapText
                             data={CashHelper.addCurrencyMask(cbsLoanDetails.pastDueInterestAmount)} />
              <TableDataCell withMinWidth wrapText data={CashHelper.addCurrencyMask(cbsLoanDetails.penaltiesAmount)} />
              <TableDataCell withMinWidth wrapText data={CashHelper.addCurrencyMask(cbsLoanDetails.customFeesAmount)} />
              {hasActionButton &&
                <TableDataCell data={
                  <>
                    <NxButton className={styles.removeButton}
                              variant={NxButtonVariant.OUTLINED}
                              onClick={(): void => removeLoan(cbsLoanDetails)}
                              disabled={isStepReadonly || (showReleaseButton && cbsLoanDetails.isLocked)}
                              loaded={!isLoading}>
                      <Trans>{`${COMMON_TRANS_PREFIX}.REMOVE`}</Trans>
                    </NxButton>
                    {showReleaseButton && cbsLoanDetails.isLocked && onReleaseProduct &&
                      <NxButton className={styles.removeButton}
                                variant={NxButtonVariant.OUTLINED}
                                onClick={(): void => onReleaseProduct(cbsLoanDetails.loanId)}
                                disabled={isStepReadonly}
                                loaded={!isLoading}>
                        <Trans>{`${HEADERS_TRANS_PREFIX}.RELEASE`}</Trans>
                      </NxButton>
                    }
                  </>
                } />
              }
              {hasWaiveCheckBox && (
                <TableDataCell data={<NxCheckbox checked={showWaive} onChange={onCheckBoxChange} />} />
              )}
            </>
          )
        }

      </TableRow>
      {!isCustomerLoansSelection && showWaive && (
        <TableRow className={styles.tableRow}>
          <TableDataCell data={<WaiveTable cbsLoanDetails={cbsLoanDetails}
                                           loading={waiveLoading}
                                           setLoading={setWaiveLoading} />} colSpan={9} />

        </TableRow>
      )}

    </>
  );
};

export default ConsolidatedLoansTableRow;
